/**
 * /admin/historico — Histórico da operação em tempo real.
 *
 * Log unificado (leads, vendas, renovações, falhas de captura, alterações de
 * config, ações de campanha, FEO Clínicas e eventos manuais). Atualiza
 * automaticamente a cada 20s, sem IA e sem gravar nada.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  AlertOctagon,
  AlertTriangle,
  Building2,
  Download,
  Info,
  Pause,
  Play,
  RefreshCw,
  Settings2,
  ShoppingBag,
  Repeat,
  Users,
  Zap,
} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { HistoricoOperacaoPanel } from "@/components/admin/HistoricoOperacaoPanel";
import { formatBR, formatBRTime } from "@/lib/datetime";
import { getOperationFeed, type FeedEvent, type FeedKind } from "@/lib/operacao/feed.functions";
import { neutralizeCsvValue } from "@/lib/csv-safe";

export const Route = createFileRoute("/admin/historico")({
  head: () => ({
    meta: [
      { title: "Admin — Histórico da Operação" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/historico">
      <HistoricoPage />
    </AdminGate>
  ),
});

const WINDOWS = [
  { id: "1h", label: "1h" },
  { id: "6h", label: "6h" },
  { id: "24h", label: "24h" },
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
] as const;

const KINDS: { id: FeedKind; label: string; icon: typeof Users; color: string }[] = [
  { id: "lead", label: "Leads", icon: Users, color: "text-sky-400" },
  { id: "venda", label: "Vendas", icon: ShoppingBag, color: "text-emerald-400" },
  { id: "renovacao", label: "Renovações", icon: Repeat, color: "text-teal-400" },
  { id: "erro", label: "Falhas", icon: AlertOctagon, color: "text-red-400" },
  { id: "config", label: "Config", icon: Settings2, color: "text-violet-400" },
  { id: "campanha", label: "Campanhas", icon: Zap, color: "text-admin-accent" },
  { id: "b2b", label: "FEO Clínicas", icon: Building2, color: "text-fuchsia-400" },
  { id: "log", label: "Eventos", icon: Info, color: "text-white/70" },
];

function kindMeta(kind: FeedKind) {
  return KINDS.find((k) => k.id === kind) ?? KINDS[KINDS.length - 1];
}

function severityIcon(s: FeedEvent["severity"]) {
  if (s === "critical") return <AlertOctagon className="h-3.5 w-3.5 text-red-400" />;
  if (s === "warn") return <AlertTriangle className="h-3.5 w-3.5 text-admin-accent" />;
  return null;
}

function HistoricoPage() {
  const feedFn = useServerFn(getOperationFeed);
  const [window_, setWindow] = useState<(typeof WINDOWS)[number]["id"]>("24h");
  const [kinds, setKinds] = useState<FeedKind[]>([]);
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(true);
  const [tick, setTick] = useState(0);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["operation-feed", window_, kinds, search],
    queryFn: () => feedFn({ data: { window: window_, kinds, search: search || undefined } }),
    refetchInterval: live ? 20_000 : false,
    refetchOnWindowFocus: live,
  });

  // relógio só para o "atualizado há X"
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 10_000);
    return () => clearInterval(t);
  }, []);
  void tick;

  const summary = data?.summary;
  const events = data?.events ?? [];

  function toggleKind(k: FeedKind) {
    setKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  function exportCsv() {
    const header = ["quando", "tipo", "severidade", "titulo", "detalhe"].join(",");
    const lines = events.map((e) =>
      [
        e.at,
        e.kind,
        e.severity,
        JSON.stringify(neutralizeCsvValue(e.title)),
        JSON.stringify(neutralizeCsvValue(e.detail ?? "")),
      ].join(","),
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `historico-operacao-${new Date().toISOString().slice(0, 16)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Voltar ao painel
        </Link>

        <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-tight">Histórico da operação</h1>
            <p className="text-sm text-white/60 mt-1">
              Log unificado do que acontece na operação — leads, vendas, falhas, configurações e campanhas.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-2 text-xs text-white/60">
              <span
                className={`h-2 w-2 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-white/30"}`}
                aria-hidden
              />
              {live ? "ao vivo (20s)" : "pausado"}
              {summary ? ` · ${formatBRTime(summary.geradoAt)}` : ""}
            </span>
            <Button variant="outline" size="sm" onClick={() => setLive((v) => !v)}>
              {live ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!events.length}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Cartões de agora */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Kpi label="Leads hoje" value={summary?.leadsHoje ?? 0} />
          <Kpi label="Leads (15 min)" value={summary?.leadsAgora ?? 0} accent="text-sky-400" />
          <Kpi label="Vendas novas hoje" value={summary?.vendasHoje ?? 0} accent="text-emerald-400" />
          <Kpi
            label="Receita hoje"
            value={(summary?.receitaHoje ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            accent="text-emerald-400"
          />
          <Kpi label="Renovações hoje" value={summary?.renovacoesHoje ?? 0} accent="text-teal-400" />
          <Kpi
            label="Falhas hoje"
            value={summary?.errosHoje ?? 0}
            accent={(summary?.errosHoje ?? 0) > 0 ? "text-red-400" : "text-white"}
          />
        </div>

        {/* Filtros */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setWindow(w.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-wider border transition-colors ${
                  window_ === w.id
                    ? "bg-white text-[#0b0a1f] border-white"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => {
              const active = kinds.length === 0 || kinds.includes(k.id);
              const Icon = k.icon;
              return (
                <button
                  key={k.id}
                  onClick={() => toggleKind(k.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    kinds.includes(k.id)
                      ? "bg-white/15 border-white/40 text-white"
                      : "border-white/10 text-white/60 hover:text-white"
                  } ${active ? "" : "opacity-50"}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${k.color}`} /> {k.label}
                </button>
              );
            })}
            {kinds.length > 0 && (
              <button onClick={() => setKinds([])} className="text-xs text-white/50 underline hover:text-white">
                limpar
              </button>
            )}
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, produto, utm, erro..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
        </div>

        {/* Timeline */}
        <div className="rounded-xl border border-white/10 bg-white/5 divide-y divide-white/5">
          {!data && <div className="p-6 text-sm text-white/50">Carregando eventos...</div>}
          {data && events.length === 0 && (
            <div className="p-6 text-sm text-white/50">Nenhum evento no período selecionado.</div>
          )}
          {events.map((e) => {
            const meta = kindMeta(e.kind);
            const Icon = meta.icon;
            return (
              <div key={e.id} className="flex gap-3 p-3.5 hover:bg-white/[0.03]">
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.color}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {severityIcon(e.severity)}
                    <span className="text-sm font-semibold">{e.title}</span>
                    {e.tags.slice(0, 3).map((t) => (
                      <Badge
                        key={t}
                        variant="outline"
                        className="border-white/15 text-white/60 text-[10px] uppercase tracking-wider"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                  {e.detail && <p className="text-xs text-white/60 mt-1 break-words">{e.detail}</p>}
                </div>
                <span className="text-[11px] text-white/40 shrink-0 tabular-nums">{formatBR(e.at)}</span>
              </div>
            );
          })}
        </div>

        {/* Log manual/técnico com filtros próprios */}
        <section className="mt-10">
          <h2 className="text-lg font-bold uppercase tracking-tight mb-3">Eventos registrados manualmente</h2>
          <HistoricoOperacaoPanel />
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "text-white" }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className={`text-lg font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
